import designCardGroup from './components/design-card-group';
import Alpine from '@alpinejs/csp';
import AsyncAlpine from 'async-alpine';
import collapse from '@alpinejs/collapse';
import intersect from '@alpinejs/intersect';
import focus from '@alpinejs/focus';
import { createRouter } from './router/swup.js';
import { initHashScroll } from './router/hash-scroll.js';
import { initFilterProgress } from './router/filter-progress.js';
import { initEnquiryForms } from './enquiry-forms.js';
import { initScrollReveals } from './transitions/scroll-reveal.js';
// NOTE: dir intentionally NOT named "analytics" — Chrome/uBlock-style
// extensions block any URL containing /analytics/, which in Vite dev mode
// kills these top-level imports → Alpine never registers → x-cloak sticks.
import { initPageviewTracking } from './signals/virtual-pageview.js';
import { initEventTracking } from './signals/events.js';
import { hydrateEnquiryContext } from './signals/enquiry-context.js';
import { hydrateUtmFields } from './signals/utm.js';

// Level 2 component imports (eagerly loaded, registered before Alpine.start())
import accordion from './components/accordion.js';
import anchorNav from './components/anchor-nav.js';
import dropdown from './components/dropdown.js';
import tabs from './components/tabs.js';
import toggle from './components/toggle.js';
import modal from './components/modal.js';
import mobileNav from './components/mobile-nav.js';
import navHeader from './components/nav-header.js';
import themeToggle from './components/theme-toggle.js';
import toastContainer from './components/toast-container.js';
import filterSelect from './components/filter-select.js';
import selectField from './components/select-field.js';
import multiSelectField from './components/multi-select-field.js';
import formModal from './components/form-modal.js';
import conditionalField from './components/conditional-field.js';
import addressAutocomplete from './components/address-autocomplete.js';
import nojsFallback from './components/nojs-fallback.js';
import listingFilter from './components/listing-filter.js';
import searchOverlay from './components/search-overlay.js';
import packagesSplitView from './components/packages-split-view.js';
import mapEmbed from './components/map-embed.js';
import copyToClipboard from './components/copy-to-clipboard.js';
import readMoreClamp from './components/read-more-clamp.js';
import scopeTabsSelect from './components/scope-tabs-select.js';
import shareAction from './components/share-action.js';
import presentationLeadTime from './components/presentation-lead-time.js';
import fileField from './components/file-field.js';

/*
 * PSD-Stack — App Bootstrap
 *
 * Initialisation order matters:
 * 1. Alpine plugins (must register before Alpine.start())
 * 2. Alpine stores (must register before Alpine.start())
 * 3. Alpine.data() component registrations
 * 4. Async Alpine (must init before Alpine.start())
 * 5. Alpine.start()
 * 6. Swup router (after DOM is ready)
 * 7. Analytics (pageview tracking + structural events + deferred GTM)
 * 8. Deferred: GSAP (requestIdleCallback)
 * 9. Deferred: Three.js (desktop only, requestIdleCallback)
 */

// 1. Register Alpine plugins
Alpine.plugin(collapse);
Alpine.plugin(intersect);
Alpine.plugin(focus);

// 2. Register Alpine stores (persist across Swup page transitions)
Alpine.store('nav', { mobileOpen: false });

Alpine.store('toasts', {
    items: [],
    counter: 0,

    add(message, type = 'info', duration = 5000) {
        const id = ++this.counter;
        this.items.push({ id, message, type });

        if (duration > 0) {
            setTimeout(() => this.remove(id), duration);
        }
    },

    remove(id) {
        this.items = this.items.filter((t) => t.id !== id);
    },

    success(message) {
        this.add(message, 'success');
    },

    error(message) {
        this.add(message, 'error', 0);
    },

    warning(message) {
        this.add(message, 'warning');
    },
});

// 3. Register Level 2 component definitions
Alpine.data('designCardGroup', designCardGroup);
Alpine.data('accordion', accordion);
Alpine.data('anchorNav', anchorNav);
Alpine.data('dropdown', dropdown);
Alpine.data('tabs', tabs);
Alpine.data('toggle', toggle);
Alpine.data('modal', modal);
Alpine.data('mobileNav', mobileNav);
Alpine.data('navHeader', navHeader);
Alpine.data('themeToggle', themeToggle);
Alpine.data('toastContainer', toastContainer);
Alpine.data('filterSelect', filterSelect);
Alpine.data('selectField', selectField);
Alpine.data('multiSelectField', multiSelectField);
Alpine.data('formModal', formModal);
Alpine.data('conditionalField', conditionalField);
Alpine.data('addressAutocomplete', addressAutocomplete);
Alpine.data('nojsFallback', nojsFallback);
Alpine.data('listingFilter', listingFilter);
Alpine.data('searchOverlay', searchOverlay);
Alpine.data('packagesSplitView', packagesSplitView);
Alpine.data('mapEmbed', mapEmbed);
Alpine.data('copyToClipboard', copyToClipboard);
Alpine.data('readMoreClamp', readMoreClamp);
Alpine.data('scopeTabsSelect', scopeTabsSelect);
Alpine.data('shareAction', shareAction);
Alpine.data('presentationLeadTime', presentationLeadTime);
Alpine.data('fileField', fileField);

// 4. Initialise Async Alpine (lazy component loading)
Alpine.plugin(AsyncAlpine);
Alpine.asyncData('imageCarousel', () => import('./components/image-carousel.js'));
Alpine.asyncData('facadeGallery', () => import('./components/facade-gallery.js'));
// Same slider module as facadeGallery — the detail_gallery island differs
// only in markup (no labels, full-bleed thumbs). Named -Slider to stay
// clear of detailGallery (the hero-gallery lightbox below).
Alpine.asyncData('detailGallerySlider', () => import('./components/facade-gallery.js'));
Alpine.asyncData('testimonialsCarousel', () => import('./components/testimonials-carousel.js'));
Alpine.asyncData('floorplan', () => import('./components/floorplan.js'));
Alpine.asyncData('detailGallery', () => import('./components/detail-gallery.js'));
Alpine.asyncData('buildZonesMap', () => import('./components/build-zones-map.js'));
Alpine.asyncData('packagesMap', () => import('./components/packages-map.js'));
// Same module — the displays map island differs only in payload (data-markers
// from {{ display_map:json }}), so it reuses the packages-map component.
Alpine.asyncData('listingsMap', () => import('./components/packages-map.js'));
Alpine.asyncData('quickLinks', () => import('./components/quick-links.js'));
// Home Finder wizard — barebone multi-step question form ending in a lead form.
Alpine.asyncData('homeFinder', () => import('./components/home-finder.js'));

// 5. Start Alpine
Alpine.start();

// Make Alpine available globally for debugging
window.Alpine = Alpine;

// 6. Initialise Swup router
const swup = createRouter();

// Jump to the URL fragment on first load (deep links / cross-site CTAs). Native
// on-load fragment scrolling is unreliable with scroll-behavior: smooth + async
// islands that reflow after paint; this fires on Swup's page:view. MOHO-40.
initHashScroll(swup);

// Loading feedback for listing filter changes — dims the results grid and runs
// the top progress bar from visit:start, so the network wait is indicated
// instead of silent.
initFilterProgress(swup);

// Re-init Alpine on Swup page navigation
swup.hooks.before('content:replace', (visit) => {
    visit.containers.forEach((selector) => {
        const container = document.querySelector(selector);
        if (container) Alpine.destroyTree(container);
    });
});
swup.hooks.on('content:replace', (visit) => {
    visit.containers.forEach((selector) => {
        const container = document.querySelector(selector);
        if (container) Alpine.initTree(container);
    });
});

// Listing-grid cascade — for in-place filter visits (visit.isQueryOnly, set in
// router/swup.js) the native viewport transition is suppressed so the nav/filter
// bar don't flash. Instead the new result cards cascade in.
//
// There is deliberately NO out-animation here. Swup skips straight to
// renderPage() when animation.animate is false, so anything hooked on
// before('content:replace') runs AFTER the fetch has already resolved — a
// fade-out there was pure added latency on top of a wait the user had already
// sat through, and it did nothing at all until GSAP's idle load finished. The
// "leaving" feedback is now the CSS grid dim, applied at visit:start so it
// overlaps the fetch instead of following it (router/filter-progress.js).
//
// GSAP loads on idle; before it's ready the visit just swaps instantly
// (graceful). Compositor-only (opacity + transform).
const listingCards = () => document.querySelectorAll('#swup .listing-grid .listing-card');

swup.hooks.on('content:replace', (visit) => {
    if (!visit.isQueryOnly || !window.gsap) return;
    const cards = listingCards();
    if (!cards.length) return;
    // True cascade: a fixed short delay BETWEEN cards (`each`, not `amount`) so
    // they visibly pop in one after another rather than all together. Capped to
    // ~1s total so a large unfiltered grid doesn't run on. fromTo sets the hidden
    // initial state synchronously (before paint), so cards never flash at full
    // opacity first.
    const each = Math.min(0.06, 1.0 / cards.length);
    window.gsap.fromTo(
        cards,
        { opacity: 0, y: 16 },
        {
            opacity: 1,
            y: 0,
            duration: 0.45,
            stagger: { each, from: 'start' },
            ease: 'power2.out',
            clearProps: 'transform,opacity',
        }
    );
});

// Card ↔ detail element morph (forward + reverse). The detail hero photo + intro
// title + spec list carry static view-transition-names (mh-hero-photo/title/specs
// — the photo inline in _hero-gallery, the other two in CSS). We give the SAME
// names to the matching parts of the ONE clicked listing card at a time, so the
// native View Transition morphs each card part into its detail counterpart — the
// card visibly "enlarges" into the detail page. Naming one card at a time avoids
// duplicate names across the grid. No-op without View Transitions → graceful
// crossfade. Fully reversible: the reverse hook re-names the target card so the
// detail parts morph back into it.
const MORPH_PARTS = [
    ['.photo', 'mh-hero-photo'],
    ['.name', 'mh-hero-title'],
    ['.specs .stats', 'mh-hero-specs'],
];
let _morphEls = [];
const clearMorph = () => {
    _morphEls.forEach((el) => {
        el.style.viewTransitionName = '';
    });
    _morphEls = [];
};
const nameCard = (card) => {
    if (!card) return;
    for (const [selector, vtName] of MORPH_PARTS) {
        const el = card.querySelector(selector);
        if (el) {
            el.style.viewTransitionName = vtName;
            _morphEls.push(el);
        }
    }
};
const isDetailUrl = (url) => /\/(home-design|display-home|house-and-land-package)\//.test(url || '');

// Forward: clicking a card names that card's parts (old snapshot = the listing).
swup.hooks.on('visit:start', (visit) => {
    if (!document.startViewTransition) return;
    clearMorph();
    const card = visit.trigger?.el?.closest?.('.listing-card');
    if (card) nameCard(card);
});

// Reverse: arriving on a listing FROM a detail page — name the card linking to
// the detail we're leaving, so the hero parts morph back into it. Runs in
// content:replace (new listing DOM in place, before the VT captures new state).
swup.hooks.on('content:replace', (visit) => {
    if (!document.startViewTransition || _morphEls.length) return;
    const from = visit.from?.url;
    if (!isDetailUrl(from)) return;
    const link = document.querySelector(`#swup .listing-card a[href="${from}"]`);
    nameCard(link?.closest('.listing-card'));
});

swup.hooks.on('visit:end', clearMorph);

// 7. Analytics
initPageviewTracking(swup);
initEventTracking(swup);

// Enquiry forms — AJAX-submit + route to the thank-you page via Swup on success
// (no full reload); no-JS falls back to the server-side {{ form:create }} redirect.
initEnquiryForms();

// Enquiry context — copy the page's #enquiry-context values into the hidden
// fields of every enquiry form, on load + after each Swup navigation (the
// embedded detail form and the persistent global contact modal).
hydrateEnquiryContext();
swup.hooks.on('page:view', () => hydrateEnquiryContext());

// UTM persistence — capture utm_* on landing, persist for the session, and copy
// into the hidden utm_* inputs of every enquiry form on load + after each nav.
hydrateUtmFields();
swup.hooks.on('page:view', () => hydrateUtmFields());

// Defer GTM to idle — Tier 4 asset. Wrapped in .catch because ad blockers
// reliably block gtm.js by filename; we don't want unhandled-rejection noise.
const loadGtmDeferred = () => {
    import('./signals/gtm.js')
        .then(({ loadGTM }) => loadGTM())
        .catch(() => {
            /* GTM blocked by extension or failed to fetch — fine in dev. */
        });
};
// Defer HubSpot tracking to idle too (Tier 4). Reads the portal id from a
// <head> meta tag (server-rendered) so one build works across environments.
const loadHubSpotDeferred = () => {
    import('./signals/hubspot.js')
        .then(({ loadHubSpot }) => loadHubSpot())
        .catch(() => {
            /* HubSpot blocked by extension or failed to fetch — fine. */
        });
};
// Defer MS Clarity (heatmaps / session recording) to idle too (Tier 4).
// Gated on VITE_CLARITY_ID — blank disables it entirely.
const loadClarityDeferred = () => {
    import('./signals/clarity.js')
        .then(({ loadClarity }) => loadClarity(import.meta.env.VITE_CLARITY_ID))
        .catch(() => {
            /* Clarity blocked by extension or failed to fetch — fine. */
        });
};
// Defer the base marketing/analytics pixels to idle too (Tier 4). Reads each
// pixel id from a <head> meta (server-rendered on production only), consent-gated
// per vendor. Ad blockers routinely block these hosts, hence the .catch.
const loadBasePixelsDeferred = () => {
    import('./signals/pixels.js')
        .then(({ loadBasePixels }) => loadBasePixels())
        .catch(() => {
            /* Pixel host blocked by extension or failed to fetch — fine. */
        });
};
// Remaining base pixels first, so their stubs (ttq / uetq) exist before GTM
// fires events that reference them. Google and Meta base tags now load via GTM.
if ('requestIdleCallback' in window) {
    requestIdleCallback(loadBasePixelsDeferred);
    requestIdleCallback(loadGtmDeferred);
    requestIdleCallback(loadHubSpotDeferred);
    requestIdleCallback(loadClarityDeferred);
} else {
    setTimeout(loadBasePixelsDeferred, 2000);
    setTimeout(loadGtmDeferred, 2000);
    setTimeout(loadHubSpotDeferred, 2000);
    setTimeout(loadClarityDeferred, 2000);
}

// ScrollTrigger lifecycle — kill instances before content swap, refresh after
swup.hooks.on('content:replace', (visit) => {
    if (visit.isHomeDesignFilter) return;
    if (window.ScrollTrigger) {
        window.ScrollTrigger.getAll().forEach((st) => st.kill());
    }
});

swup.hooks.on('page:view', (visit) => {
    if (window.ScrollTrigger) {
        window.ScrollTrigger.refresh();
    }
    if (!visit.isHomeDesignFilter) initScrollReveals();
});

// 8. Deferred: GSAP + ScrollTrigger (loaded on first idle moment)
function loadGsap() {
    Promise.all([
        import('gsap'),
        import('gsap/ScrollTrigger'),
        import('gsap/Flip'),
    ]).then(([{ gsap }, { ScrollTrigger }, { Flip }]) => {
        gsap.registerPlugin(ScrollTrigger, Flip);
        window.gsap = gsap;
        window.ScrollTrigger = ScrollTrigger;
        window.Flip = Flip;
        initScrollReveals();
    });
}

if ('requestIdleCallback' in window) {
    requestIdleCallback(loadGsap);
} else {
    setTimeout(loadGsap, 1000);
}
